/* eslint-disable no-nested-ternary */
import { useContext } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'gatsby-plugin-react-intl';

import { PromotionContext } from '../../Providers/Promotion';

import { InternalStyledLink, ExternalStyledLink } from './styles';

const hasExtension = url => {
  const parts = url.split('/');
  const last = parts.pop();
  return parts.length > 3 && last.indexOf('.') !== -1;
};

const isLink = url => {
  return url.indexOf('/');
};

const setPromotion = (url, promotion, isPromoTrue, overridePromo = '') => {
  const { promo, RAF, affiliate, defaultRAFPromoCode, contentType } = promotion;
  // trimmed url
  const sanitizedURL = url.trim();
  // split url on question mark in cases where ctas have existing parameters
  const urlSplit = sanitizedURL.split('?');
  // base url without parameters
  const baseUrl = urlSplit[0];
  // null check to see if there are parameters, default to empty string if none
  const parameters = urlSplit[1] ? `&${urlSplit[1]}` : '';
  // * Link is not promotional. Don't set promotion url
  // * Also check if link ends with / or not and append on case
  // * REFACTOR NECESSARY TO CLEAN THIS UP - NOV 19TH 2020
  // not a promotion links so use sanitizedURL - June 15th 2020
  if (!isPromoTrue)
    return hasExtension(sanitizedURL) ||
      isLink(sanitizedURL) ||
      sanitizedURL.endsWith('/') ||
      sanitizedURL.includes('/search-results')
      ? sanitizedURL
      : `${sanitizedURL}/`;

  const affiliateId = affiliate
    ? `&${affiliate.affiliateCode}=${affiliate.affiliateId}`
    : '';

  // if ctas data comes with a promcode, override default and base page
  const promocode = overridePromo || promo;

  // * If we don't have a refer a friend code, just use the promoCode
  // * If we have a RAF code, and the current page we are on is a campaign page, use the promoCode + RAF code
  // * If we have a RAF code, but not on a campaign page, use the default RAF promocode
  return !RAF
    ? `${baseUrl}/?sourceCode=INT&promoCode=${promocode}${affiliateId}${parameters}`
    : contentType === 'ContentfulCampaignPageLayout'
      ? `${baseUrl}/?sourceCode=INT&promoCode=${promocode}${affiliateId}&referralCode=${RAF}${parameters}`
      : `${baseUrl}/?sourceCode=INT&promoCode=${defaultRAFPromoCode}${affiliateId}&referralCode=${RAF}${parameters}`;
};

const handleClickToLink = (url, event) => {
  if (/^#/.test(url)) {
    event.preventDefault();
    // offset is the height of the header
    const offset = 70;
    const element = document.querySelector(event.currentTarget.hash);
    const offsetPosition = element.getBoundingClientRect().top - offset;
    window.scrollBy({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
};

const Link = ({
  data = null,
  to = '',
  type = '',
  ariaLabel = null,
  title = null,
  className = '',
  children,
  state = null,
  blade = false,
  resetState = null,
}) => {
  const href = to || (data.links ? data.links : data.link);
  const style = type || (data ? data.type : '');
  const aria = ariaLabel || (data ? data.ariaLabel : '');
  // Reference of ID is not useful as it doesn't help tagging for analytics
  // const identification = id || (data ? data.id : '');
  const size = data && data.ctaButtonSize ? data.ctaButtonSize : null;
  const src =
    data && data.pdfIconDam
      ? data.pdfIconDam
      : data && data.iconDam
        ? data.iconDam.asset.localImage.publicURL
        : null;
  const hoverIcon =
    data && data.hoverIconDam
      ? data.hoverIconDam.asset.localImage.publicURL
      : null;
  const asset =
    data && data.ctaPdfLinkDam
      ? data.ctaPdfLinkDam.asset.localImage.publicURL
      : null;
  const isAPromo = data && data.isPromo ? data.isPromo : null;
  const placement =
    data && data.iconPlacement ? JSON.stringify(data.iconPlacement) : 'false';

  const intl = useIntl();
  const isInternal = /^(\/(?!\/)|#)/.test(href);
  const isSpecialLink = /\btel:\b|\bmailto:\b/.test(href);
  const promotionContext = useContext(PromotionContext);
  const analyticTracker =
    data && data.analyticTracker ? data.analyticTracker : '';
  const analyticLocation =
    data && data.analyticLocation ? data.analyticLocation : '';
  const contentfulId = data && data.contentful_id ? data.contentful_id : '';
  const overridePromo = data && data.promocode ? data.promocode : '';

  if (href && href.toLowerCase() === '#dropdown') {
    return null;
  }

  const dataLayerPush = () => {
    window.dataLayer = window.dataLayer || [];
    const gtmData = {
      event: 'cta_click', // cta click event name
      cta_page: window.location.pathname, // page path
      cta_section: analyticLocation, // CMS analytic location field
      cta_id: `${aria} - ${contentfulId}`, // aria label text + contentful id
      cta_url: href, // href of url
    };
    window.dataLayer.push(gtmData);
  };

  return isInternal ? (
    <InternalStyledLink
      state={state}
      to={
        asset || setPromotion(href, promotionContext, isAPromo, overridePromo)
      }
      className={`${className} ${style} ${size}`}
      icon={src}
      hovericon={hoverIcon}
      placement={placement}
      aria-label={aria}
      title={title}
      data-cta-id={analyticTracker}
      data-cta-location={analyticLocation}
      data-contentful={contentfulId}
      onClick={event => {
        handleClickToLink(href, event);
        dataLayerPush();
        // pass in blade context from header to close blade if current page url is clicked
        if (blade) {
          resetState();
        }
      }}
    >
      <span>{children}</span>
      {!src &&
      className !== 'remove-svg' &&
      className !== 'unstyled-link' &&
      className !== 'rich-text-link' ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          fill="none"
          viewBox="0 0 16 16"
        >
          <g fill="currentColor">
            <path d="M13.335 8.1L5.557.32 4.206 1.673c-.274.273-.274.716 0 .99l5.437 5.436-5.438 5.438c-.273.273-.273.716 0 .99l1.352 1.35 5.932-5.931 1.013-1.013.833-.833z" />
          </g>
        </svg>
      ) : null}
    </InternalStyledLink>
  ) : (
    <ExternalStyledLink
      state={state}
      href={
        asset || setPromotion(href, promotionContext, isAPromo, overridePromo)
      }
      className={`${className} ${style} ${size}`}
      icon={src}
      hovericon={hoverIcon}
      placement={placement}
      aria-label={aria}
      title={title}
      isSpecialLink={isSpecialLink}
      data-cta-id={analyticTracker}
      data-cta-location={analyticLocation}
      data-contentful={contentfulId}
      aria-describedby="new-window-0"
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        dataLayerPush();
      }}
    >
      <span>
        {children}{' '}
        <span className="sr-only">
          {intl.formatMessage({ id: 'externalLink' })}
        </span>
        {!src && className !== 'unstyled-link' ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="5 5 15 15"
          >
            <title>
              {intl.locale === 'en' ? 'External link' : 'Lien externe'}
            </title>
            <g
              fill="none"
              fillRule="evenodd"
              stroke="currentColor"
              strokeLinecap="round"
              strokeWidth="2"
            >
              <path
                d="M13 0L6.067 7.008M7.8 0L13 0 13 5.25"
                transform="translate(6 6)"
              />
              <path
                strokeLinejoin="round"
                d="M3.404 2H2C.895 2 0 2.895 0 4v7c0 1.105.895 2 2 2h7c1.105 0 2-.895 2-2V8.77h0"
                transform="translate(6 6)"
              />
            </g>
          </svg>
        ) : null}
      </span>
    </ExternalStyledLink>
  );
};

Link.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
  data: PropTypes.shape(),
  to: PropTypes.string,
  type: PropTypes.string,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  title: PropTypes.string,
  state: PropTypes.shape(),
  blade: PropTypes.bool,
  resetState: PropTypes.func,
};

export default Link;
